import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { Button } from '@mui/material';
import { EmptyState } from 'src/components/atlas/molecules/EmptyState';
import { useCategoryConfigurationRoute } from 'src/hooks/useCategoryConfigurationRoute';
import { useTheme } from 'styled-components';
import {
  CreateFormEmptyStateButton,
  EmptyStateWrapper,
  ErrorStateWrapper,
  FormManagementCategoryConfigurationLink,
} from './CoiFormManagement.styles';
import {
  FormManagementCategorySections,
  type FormManagementCategorySectionsProps,
} from './FormManagementCategorySections';
import {
  FormsListSection,
  type FormsListSectionProps,
} from './FormsListSection';

export type CoiFormManagementMainContentProps = {
  isError: boolean;
  refetch: () => void;
  showNoFormsYetWithCategories: boolean;
  showNoCategoriesEmpty: boolean;
  onOpenCreateFormModal: () => void;
  categorySectionsProps: FormManagementCategorySectionsProps;
  formsListProps: FormsListSectionProps;
};

export function CoiFormManagementMainContent({
  isError,
  refetch,
  showNoFormsYetWithCategories,
  showNoCategoriesEmpty,
  onOpenCreateFormModal,
  categorySectionsProps,
  formsListProps,
}: CoiFormManagementMainContentProps) {
  const theme = useTheme();
  const categoryConfigRoute = useCategoryConfigurationRoute();
  if (isError) {
    return (
      <ErrorStateWrapper data-testid="form-management-error-state">
        <EmptyState
          title="Unable to load forms"
          description="Something went wrong while loading forms. Please try again."
          cta={
            <Button
              variant="contained"
              onClick={() => refetch()}
              data-testid="form-management-error-state-content-button"
            >
              Retry
            </Button>
          }
        />
      </ErrorStateWrapper>
    );
  }

  if (showNoFormsYetWithCategories) {
    return (
      <EmptyStateWrapper data-testid="form-management-no-forms-yet-state">
        <EmptyState
          title="No forms created yet"
          description="When you create forms, they will appear here."
        />
        <CreateFormEmptyStateButton
          type="button"
          onClick={onOpenCreateFormModal}
          data-testid="form-management-create-form-from-empty"
        >
          Create your first form
          <StyledIcon
            className="uil uil-arrow-right"
            size="24px"
            color={theme.atlas.semantic.color.type.inverse}
            aria-hidden
          />
        </CreateFormEmptyStateButton>
      </EmptyStateWrapper>
    );
  }

  if (showNoCategoriesEmpty) {
    return (
      <EmptyStateWrapper data-testid="form-management-empty-state">
        <EmptyState
          title="No categories found"
          description="There are no categories configured to create a form."
        />
        <FormManagementCategoryConfigurationLink
          to={categoryConfigRoute}
          data-testid="form-management-category-configuration-link"
        >
          Go to category configuration
          <StyledIcon
            className="uil uil-arrow-right"
            size="24px"
            color={theme.atlas.semantic.color.type.inverse}
            aria-hidden
          />
        </FormManagementCategoryConfigurationLink>
      </EmptyStateWrapper>
    );
  }

  return (
    <>
      <FormManagementCategorySections {...categorySectionsProps} />
      <FormsListSection {...formsListProps} />
    </>
  );
}
